import * as THREE from 'three';
import { rectangle } from '../math/rectangle';
import { boundary } from '../math/boundary';
import { Line2 } from '../math/Line2';
import * as marietta from '../..';
import PointList from './PointList';
import Graph from './Graph';

class Collision {
    public line: [number, number];

    public index: number;

    public position: THREE.Vector2;

    public distance: number;

    constructor(line: [number, number], position: THREE.Vector2, distance: number) {
      this.line = line;
      this.index = -1;
      this.position = position;
      this.distance = distance;
    }
}

class CollisionList {
    public line: [number, number];

    public list: Collision[];

    constructor(line: [number, number]) {
      this.line = line;
      this.list = [];
    }
}

class status {
    public index: number;

    public status: number;
}

class divideInfo {
    public index: number;

    public distance: number;

    constructor(index: number, distance: number) {
      this.index = index;
      this.distance = distance;
    }

    private AddPoint() {
      return this.index === -1;
    }

    public isStartPoiint() {
      return this.AddPoint() && this.distance === 0.0;
    }

    public isEndPoint() {
      return this.AddPoint() && this.distance === 1.0;
    }
}

interface LineInfo {
    start: divideInfo;
    end: divideInfo;
}

const STATUS = {
  BRANCH: -1,
  OUTER: -2,
  NAN: -3,
} as const;

const EPSILON = 0.00001;

export default class GeometoryBuilder {
    public pointlist: PointList = new PointList();

    public graph: Graph = new Graph();

    public addPoint(p: THREE.Vector2) {
      this.pointlist.points.push(p);
      const index = this.pointlist.points.length - 1;

      if (index >= this.graph.adjacencyList.length) {
        this.graph.addNode();
      }
      return index;
    }

    public addLine(line: Line2): void {
      const distance = line.distance();
      if (distance < EPSILON) return; // 線の長さがEPSILON以下なら

      // 線分の分割リストの取得
      const divideList = this.divideLine(line);

      // 分割リストdivideListを元に新規の線を追加

      const addPoint = (info: divideInfo, line: Line2) => {
        if (info.isStartPoiint()) return this.addPoint(line.start);
        if (info.isEndPoint()) return this.addPoint(line.end);
        return info.index;
      };

      divideList.forEach((d: LineInfo) => {
        const index1 = addPoint(d.start, line);
        const index2 = addPoint(d.end, line);

        this.graph.link(index1, index2);

        if (d.start.index != index1) d.start.index = index1;
        if (d.end.index != index2) d.end.index = index2;
      });

      //
      this.appendPoint(divideList);

      this.updateGraph();
    }

    private checkCollision(bouns: boundary | rectangle, p: THREE.Vector2) {
      return bouns.min.x - EPSILON < p.x && p.x < bouns.max.x + EPSILON && bouns.min.y - EPSILON < p.y && p.y < bouns.max.y + EPSILON;
    }

    private checkLine(a: THREE.Vector2, b: THREE.Vector2): boolean {
      for (let i = 0; i < this.graph.adjacencyList.length; i++) {
        const nodes = this.graph.adjacencyList[i];
        for (let j = 0; j < nodes.length; j++) {
          const pIndex = nodes[j];
          if (pIndex < i) continue;

          const rec = new rectangle(this.pointlist.points[i], this.pointlist.points[pIndex]);
          if (!this.checkCollision(rec, a) || !this.checkCollision(rec, b)) continue;
          if (this.calcLine(this.pointlist.points[i], this.pointlist.points[pIndex], a, b)) return true;
        }
      }
      return false;
    }

    private calcLine(a: THREE.Vector2, b: THREE.Vector2, c: THREE.Vector2, d: THREE.Vector2): boolean {
      return (Math.abs(this.calcDistance(a, b, c)) < EPSILON) && (Math.abs(this.calcDistance(a, b, d)) < EPSILON);
    }

    // 線分a-bとcの距離
    private calcDistance(a: THREE.Vector2, b: THREE.Vector2, c: THREE.Vector2): number {
      const p1 = a.clone().sub(c);
      const p2 = b.clone().sub(a);
      const d = p2.length();
      return p1.cross(p2) / d;
    }

    private divideLine(line: Line2): LineInfo[] {
      const infoArray = this.divideList(line); // 線分の上にある点より分割候補のリストを作成

      // line上に点がない　若くはline上の先頭の点がlineの始点よりもEPSILON以上離れているなら
      if (!infoArray.length || !(Math.abs(infoArray[0].distance) < EPSILON)) {
        infoArray.unshift(new divideInfo(-1, 0)); // 始点候補の追加
      }
      // 分割候補の末端がline終点との距離がイプシロン以上離れているなら
      if (!(Math.abs(infoArray[infoArray.length - 1].distance - 1.0) < EPSILON)) {
        infoArray.push(new divideInfo(-1, 1.0)); // 終点候補の追加
      }

      const getPosition = (info: divideInfo) => {
        if (info.isStartPoiint()) return line.start;
        if (info.isEndPoint()) return line.end;
        return this.pointlist.points[info.index];
      };

      const LineArray: LineInfo[] = [];

      for (let i = 1; i < infoArray.length; i++) {
        const info1 = infoArray[i - 1];
        const info2 = infoArray[i];

        // 線分の上に線分が乗っているか
        const res = this.checkLine(getPosition(info1), getPosition(info2));
        // 構築されている線分リストの線分上にlineの始点終点 ２点が乗る(並行で始点終点に収まる)場合、分割リストに含めない
        if (!res) {
          LineArray.push({ start: info1, end: info2 });
        }
      }
      return LineArray;
    }

    private divideList(line: Line2): divideInfo[] {
      const pointIndexes = this.pointlist.getIndexesFromLine(line); // 線分 lineの上にある点のインデックスを取得
      // 線分の距離を計算
      // p1と点群との距離を計りスケール(0.0 - 1.0)
      const d = line.distance();
      const distanceList = pointIndexes.map((index) => new divideInfo(index, line.start.distanceTo(this.pointlist.points[index]) / d));

      // スケール(0.0 - 1.0)された距離でソート
      return distanceList.sort((n1: divideInfo, n2: divideInfo) => {
        if (n1.distance > n2.distance) return 1;
        if (n1.distance < n2.distance) return -1;
        return 0;
      });
    }

    private lineCut(collisionList: CollisionList[]) {
      // 交差リストを元に点を追加してグラフを更新
      collisionList.forEach((cols: CollisionList) => {
        // 既に追加されている線分を分割
        cols.list.forEach((col: Collision) => {
          if (Math.abs(col.distance) < EPSILON) { // 始点
            col.index = cols.line[0];
          } else if (Math.abs(col.distance - 1) < EPSILON) { // 終点
            col.index = cols.line[1];
          } else {
            col.index = this.addPoint(col.position);
          }
          this.graph.split(col.line[0], col.line[1], col.index);
        });

        // 新規追加の線分の分割
        cols.list.sort((n1: Collision, n2: Collision) => {
          if (n1.distance > n2.distance) return 1;
          if (n1.distance < n2.distance) return -1;
          return 0;
        });
        if (!cols.list.length) return;

        // 始点、終点に交差がある場合
        if (Math.abs(cols.list[0].distance) < EPSILON) {
          cols.list.shift();
          if (!cols.list.length) return;
        }
        if (Math.abs(cols.list[cols.list.length - 1].distance - 1) < EPSILON) {
          cols.list.pop();
          if (!cols.list.length) return;
        }

        const indexes = cols.list.map((col: Collision) => col.index);
        indexes.unshift(cols.line[0]);
        indexes.push(cols.line[1]);

        this.graph.unlink(cols.line[0], cols.line[1]);
        this.graph.unlink(cols.line[1], cols.line[0]);

        for (let i = 1; i < indexes.length; i++) {
          this.graph.link(indexes[i - 1], indexes[i]);
        }
      });
    }

    // 新規で追加された線分のリストに対して交差する線を求め、点を追加してグラフを更新
    private appendPoint(lineList: LineInfo[]) {
      // 新規で追加された線と既存の線とで交差判定

      const collisionList: CollisionList[] = [];

      lineList.forEach((line: LineInfo) => {
        const p1 = this.pointlist.points[line.start.index];
        const p2 = this.pointlist.points[line.end.index];
        const line1 = new Line2(p1, p2); // 新規追加線候補

        const collist = new CollisionList([line.start.index, line.end.index]);

        this.graph.adjacencyList.forEach((nodes: number[], index: number) => {
          const pa = this.pointlist.points[index];

          // 新規線分と、既存の線を構成する点の接触は対象外
          // 線と点の距離を求め、イプシロンの範囲なら接触とする
          const d1 = this.calcDistance(line1.start, line1.end, pa);
          if (Math.abs(d1) < EPSILON) return;

          nodes.forEach((pIndex: number) => {
            if (index > pIndex) return;
            if ((line.start.index == index || line.start.index == pIndex) && (line.end.index == index || line.end.index == pIndex)) return;

            const pb = this.pointlist.points[pIndex];

            // 線分と点の接触は対象外
            const d2 = this.calcDistance(line1.start, line1.end, pb);
            if (Math.abs(d2) < EPSILON) return;

            const line2 = new Line2(pa, pb); // 追加されているポイントから線データーを作成

            // 新規線分と既存の線分line1とline2との交差判定
            let d3 = this.calcDistance(line2.start, line2.end, p1);
            let d4 = this.calcDistance(line2.start, line2.end, p2);
            if (Math.abs(d3) < EPSILON) d3 = 0.0;
            if (Math.abs(d4) < EPSILON) d4 = 0.0;
            if (d1 * d2 > 0 || d3 * d4 > 0) return;

            const result1 = line1.crossPoint(line2);
            if (!result1) return;
            const result = line2.crossPoint(line1);
            if (!result) return;
            if (Number.isNaN(result.x) || Number.isNaN(result.y)) return;

            collist.list.push(new Collision([index, pIndex], result, p1.distanceTo(result) / line1.distance()));
          });
        });
        collisionList.push(collist);
      });

      this.lineCut(collisionList); // 交差リストを元に点を追加してグラフを更新
    }

    // vec(0,1)の12時方向を基準として時計回りでGraph情報を再構築する
    public updateGraph() {
      const vec = new THREE.Vector2(0, 1);

      const update = this.graph.updateList;

      for (let i = 0; i < update.length; i++) {
        if (update[i] == false) continue;

        const vec1 = this.pointlist.points[i];
        const nodes = this.graph.adjacencyList[i];

        const plist = [];

        for (let j = 0; j < nodes.length; j++) {
          const index = nodes[j];
          const vec2 = this.pointlist.points[index].clone().sub(vec1).normalize();
          let rad = Math.acos(vec.dot(vec2));
          if (vec.cross(vec2) < 0.0) {
            rad = 2 * Math.PI - rad;
          }
          plist.push({ index, radian: rad });
        }
        plist.sort((n1: any, n2: any) => {
          if (n1.radian > n2.radian) return 1;
          if (n1.radian < n2.radian) return -1;
          return 0;
        });
        plist.map((p: any) => p.index).forEach((num: any, index: any) => nodes[index] = num);
      }
      this.graph.clearUpdate();
    }

    // X座標を小さい順に並べる
    // xが同じ値の場合、Yが大き方順に並べる
    private pointsort() {
      const plist = this.pointlist.points.map((p: THREE.Vector2, index: number) => ({ point: p, index }));

      plist.sort((p1: any, p2: any) => {
        if (p1.point.x > p2.point.x) return 1;
        if (p1.point.x < p2.point.x) return -1;
        if (p1.point.y < p2.point.y) return 1;
        if (p1.point.y > p2.point.y) return -1;
        return 0;
      });
      return plist;
    }

    // エリア番号の最大値を取得
    private maxNumber(array: number[]) {
      return array.reduce((acc: any, value: any) => (acc > value ? acc : value));
    }

    // グラフとポイントリストから外周を求める
    public outer() {
      const nodeList = this.graph.adjacencyList;
      const branch = this.graph.branch();

      const isBranch = (index: number) => branch[index] != -1;

      const nextPoint = (index: number, pos: number) => {
        const nodes = nodeList[index];
        let result = 0;

        for (let i = 0; i < nodes.length; i++) {
          result = nodes[(pos + i) % nodes.length];
          if (!isBranch(result)) break;
        }
        return result;
      };
      const pointIndex = (index: number, pos: number) => {
        const num = nodeList[index].indexOf(pos) + 1;
        return nextPoint(index, num);
      };

      const isloop = (indexList: number[], currentIndex: number, nextIndex: number) => {
        const num = indexList.length;

        for (let i = 0; i < num - 1; i++) {
          const n1 = indexList[i];
          const n2 = indexList[(i + 1)];

          if (n1 == currentIndex && n2 == nextIndex) return true;
        }
        return false;
      };

      const group = this.graph.group();
      if (!group.length) return [];

      const plist = this.pointsort();

      // グループ番号の最大値
      const gMax = this.maxNumber(group);

      const outerList = [];

      for (let i = 0; i <= gMax; i++) {
        const outer = [];
        // グループ番号iのリスト
        const list = plist.filter((p: any) => group[p.index] == i && !isBranch(p.index));

        if (!list.length) continue;

        let currentindex = list[0].index;
        let nextIndex = nextPoint(currentindex, 0);

        while (!isloop(outer, currentindex, nextIndex)) {
          outer.push(currentindex);
          const index = pointIndex(nextIndex, currentindex);
          currentindex = nextIndex;
          nextIndex = index;
        }
        outerList.push(outer);
      }
      return outerList;
    }

    // Graph情報を複製しつつ、枝と外周情報を追加する
    // STATUS.BRANCH(-3)
    // STATUS.OUTER(-2)

    private createStatus(): status[][] {
      const branch = this.graph.branch(); // 枝番号の算出
      const nodeList = this.graph.adjacencyList;

      // 枝情報の書き込み
      const isBranch = (index: number) => branch[index] != -1;

      const statuslist: status[][] = nodeList.map((nodes: any, index1: number) => nodes.map((pIndex: number) => {
        const s = new status();
        s.index = pIndex;
        s.status = (isBranch(index1) || isBranch(pIndex) ? STATUS.BRANCH : STATUS.NAN);
        return s;
        // return { index: pIndex, status: (isBranch(index1) || isBranch(pIndex) ? STATUS.BRANCH : STATUS.NAN) };
      }));

      // 外周情報の書き込み
      this.outer().forEach((outer: any) => {
        let currentIndex = outer[0];
        for (let i = 1; i <= outer.length; i++) {
          const nextIndex = outer[i % outer.length];

          const index = nodeList[currentIndex].indexOf(nextIndex);
          statuslist[currentIndex][index].status = STATUS.OUTER;
          currentIndex = nextIndex;
        }
      });

      return statuslist;
    }

    private setStates(statusList: status[][], indexList: number[], areaNumber: number) {
      let currentIndex = indexList[0];
      for (let i = 1; i <= indexList.length; i++) {
        const nextIndex = indexList[i % indexList.length];
        const index = statusList[currentIndex].findIndex((element: any) => element.index === nextIndex);
        statusList[currentIndex][index].status = areaNumber;
        currentIndex = nextIndex;
      }
    }

    // 領域の算出
    public area(): THREE.Vector2[][] {
      const areaList = this.area_();
      const result: THREE.Vector2[][] = [];

      areaList.forEach((area: number[], index: number) => {
        const res = [];
        for (let i = 0; i < area.length; i++) {
          res.push(this.pointlist.points[area[i]]);
        }
        result.push(res);
      });

      // 無限ループに入りそうになったら、アラートを表示
      if (marietta.webcad.WebCADGlobal.scene.objects.find((obj) => obj.type == 'ClosePath') && result.length == 0) {
        marietta.webcad.WebCADGlobal.showAlert('ジオメトリに問題がありました', '警告');
      }

      return result;
    }

    private area_(): number[][] {
      const statusList = this.createStatus();

      // node-indexに繋がっているnodeの一番最後の値を取得
      const findlastindexof = (index: number) => {
        const nodes = statusList[index];
        // 外周、枝、検索済みのnodeは含まない
        for (let i = 1; i <= nodes.length; i++) {
          const node = nodes[nodes.length - i]; // 配列の後ろから取得
          if (node.status == STATUS.NAN) return node.index;
        }
        return -1;
      };

      const points = this.pointsort(); //
      let counter = 0;

      const areaList: number[][] = [];

      while (counter < points.length) {
        let currentIndex = points[counter].index; // エリアのスタートインデックス
        let nextIndex = findlastindexof(currentIndex); // 次のインデックス

        if (nextIndex == -1) {
          counter++;
          continue;
        }
        const startIndex = currentIndex;
        const range_2 = nextIndex;

        let area: number[] = [];
        area.push(currentIndex);

        // ループ末端チェック
        const isRoop = (index) => {
          if (area[0] == index) { // エリアインデックスの先頭と同じ値ならループ
            return true;
          }
          return false;
        };

        // areaの算出
        while (!isRoop(nextIndex)) {
          const index = this.findIndex(statusList[nextIndex], currentIndex);
          currentIndex = nextIndex;
          nextIndex = index;
          area.push(currentIndex);

          if (area.length % 1000 == 0) {
            if (area.length >= 10000) return [];
            if (this.checkLoop(area)) return [];
          }
        }

        // areaの始点より分岐したエリアの処理
        let innerpoint_ = currentIndex;
        let innerArray: number[] = [];

        while (!(innerpoint_ == -1)) {
          const array = this.innerLoop(statusList, startIndex, innerpoint_, range_2);

          if (array.length >= 10000) {
            return [];
          }

          innerArray = [...innerArray, ...array];

          if (innerArray.length >= 10000) {
            return [];
          }

          if (array.length) {
            innerpoint_ = array[array.length - 1];
          }

          innerpoint_ = this.innerPoint(statusList[startIndex], innerpoint_, range_2);
        }

        if (innerArray.length) {
          area = [...area, ...innerArray];
        }

        // 算出された領域情報をstatusListへ書き込む
        this.setStates(statusList, area, areaList.length);

        areaList.push(area);
      }
      return areaList;
    }

    private checkLoop(nums: number[]) {
      // const test = [0,1,2,3,4,5,0,1,2,3,0,1,2,3,4,5,0,1,2,0,1,2,3,4,5,0,1,2,3,0,1,2,3,4,5,0,1,2,0]
      // const rnum = [...test].reverse();

      // ループしている時は配列の末尾の値が、配列のそれより前にも同値が存在し、
      // その位置より前にも存在する
      // 末尾の値とそれより前に存在する末尾と同じ値との間の値群がそれより前で繰り返されている

      // 検出を簡単にする為、配列の反転
      const rnum = [...nums].reverse();

      const num = rnum[0];
      const posarray: number[] = [];

      // 先頭の値と同じ値を持つ場所を記録
      for (let i = 0; i < rnum.length; i++) {
        if (num == rnum[i]) {
          posarray.push(i);
        }
      }
      if (posarray.length < 3) return false;

      // 先頭の値と次に同じ値が出現する場所のまで配列を作り、
      // 先の同じ値が出現する場所から再び同じ値が出現する場所までの配列を作り比較 => 同じならループ
      // 先頭の値と同じ値が出現する二回目場所のまで配列を作り、
      // 二回目の場所から４回目に出現する場所までの配列で比較
      // これを配列同士が一致するまで以下の様に広げていく
      // 0-1, 1-2
      // 0-2, 2-4
      // 0-3, 3-6
      // 0-4, 4-8 と広げていく
      let n = 1;

      while (n * 2 < posarray.length) {
        const ary1 = rnum.slice(posarray[0], posarray[n]);
        const ary2 = rnum.slice(posarray[n], posarray[n * 2]);

        if (ary1.length == ary2.length) {
          const ary = ary1.filter((val, index) => ary2[index] == val);
          if (ary1.length == ary.length) return true;
        }
        n++;
      }
      return false;
    }

    // status内range1よりrange2の範囲内でSTATUS.NANを探す
    private innerPoint(status_: status[], range1: number, range2: number) {
      const pos1 = status_.findIndex((s: status) => s.index === range1);

      for (let i = 1; i <= status_.length; i++) {
        const node = status_[(pos1 + i) % status_.length];

        if (node.index == range2) return -1;
        if (node.status == STATUS.NAN) return node.index;
      }
      return -1;
    }

    private findIndex(status_: status[], currentIndex: number) {
      const index = status_.findIndex((s: status) => s.index === currentIndex);
      if (index == -1) return -1;

      for (let i = 0; i < status_.length; i++) {
        const node = status_[(index + 1 + i) % status_.length];
        if (node.status == STATUS.NAN) return node.index;
      }
      return -1;
    }

    // エリアの始点から分岐しているケースへの対応
    private innerLoop(statusList: status[][], startIndex: number, range1: number, range2: number): number[] {
      const index = this.innerPoint(statusList[startIndex], range1, range2);
      if (index == -1) return [];

      let currentIndex = startIndex;
      let nextindex = index;

      const array: number[] = [];
      array.push(startIndex);

      while (!(nextindex == startIndex)) {
        array.push(nextindex);

        // 部屋囲みの頂点数が１００００を超えていたらエラー
        if (array.length > 10000) {
          return array;
        }

        const index = this.findIndex(statusList[nextindex], currentIndex);
        if (index == -1) return [];

        currentIndex = nextindex;
        nextindex = index;
      }
      return array;
    }

    public edges(): Line2[] {
      const { points } = this.pointlist;

      const result: Line2[] = [];

      this.graph.adjacencyList.forEach((edges: number[], index: number) => {
        edges.forEach((pointIndex: number) => {
          if (index > pointIndex) return;
          result.push(new Line2(points[index], points[pointIndex]));
        });
      });
      return result;
    }

    public vertices(): THREE.Vector2[] {
      return this.graph.adjacencyList.map((edges: any, index: number) => this.pointlist.points[index]);
    }

    public group() {
      return this.graph.group();
    }
}
